// Zaměstnanci (F1 1.5, backend/README.md „Účty a oprávnění“): seznam s filtry a hledáním,
// karta, nastavení účtu a oprávnění, sekce navíc per zaměstnanec (08 §1 bod 4, rozhodnutí 13. 9. večer),
// ukončení/obnovení pracovního poměru; F2 2.7 založení (POST /zamestnanci) a úprava karty
// (PUT /zamestnanci/{id}). Osobní a mzdová pole vrací server jen s právem — UI je zobrazí, jen když přijdou.
import type {
  components,
  operations,
  RoleNavicVstup,
  RolePrehled,
  SekceNavicPrehled,
  SekceNavicVstup,
  Zamestnanec,
  ZamestnanecSeznam,
  ZamestnanecUcetVstup,
  ZamestnanecUkonceniVstup,
} from '@ecoser/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api } from './klient';

export type {
  RolePrehled,
  SekceNavicPrehled,
  Zamestnanec,
  ZamestnanecSeznam,
  ZamestnanecUcetVstup,
};

/** Tělo POST /zamestnanci a PUT /zamestnanci/{id} (schéma ZamestnanecVstup, celý vstup). */
export type ZamestnanecVstup = components['schemas']['ZamestnanecVstup'];

export const KLIC_ZAMESTNANCI = ['zamestnanci'] as const;

/** Výchozí velikost stránky seznamu (firma má desítky lidí — jedna stránka stačí). */
export const VELIKOST_SEZNAMU = 50;

export interface FiltrZamestnancu {
  hledat?: string;
  /** false = jen aktivní (výchozí v seznamu), true = i ukončené. */
  iNeaktivni?: boolean;
  /** Filtr sloupce Role (klíč role). */
  role?: string;
  /** Řazení `pole:asc|desc` (prijmeni, email, role, nastup); bez něj příjmení a jméno. */
  razeni?: string;
  strana?: number;
}

function dotazSeznamu(filtr: FiltrZamestnancu): string {
  const q = new URLSearchParams();
  q.set('strana', String(filtr.strana ?? 1));
  q.set('velikost', String(VELIKOST_SEZNAMU));
  q.set('razeni', filtr.razeni ?? 'prijmeni:asc,jmeno:asc');
  if (filtr.role) q.set('role', filtr.role);
  if (filtr.hledat?.trim()) q.set('hledat', filtr.hledat.trim());
  if (!filtr.iNeaktivni) q.set('aktivni', 'true');
  return q.toString();
}

export function nacistZamestnance(
  filtr: FiltrZamestnancu,
  signal?: AbortSignal,
): Promise<ZamestnanecSeznam> {
  return api.get<ZamestnanecSeznam>(`/zamestnanci?${dotazSeznamu(filtr)}`, { signal });
}

export function useZamestnanci(filtr: FiltrZamestnancu) {
  return useQuery({
    queryKey: [...KLIC_ZAMESTNANCI, 'seznam', filtr],
    queryFn: ({ signal }) => nacistZamestnance(filtr, signal),
    placeholderData: (predchozi) => predchozi,
  });
}

export function nacistZamestnanceKartu(id: string, signal?: AbortSignal): Promise<Zamestnanec> {
  return api.get<Zamestnanec>(`/zamestnanci/${encodeURIComponent(id)}`, { signal });
}

export function useZamestnanec(id: string | undefined) {
  return useQuery({
    queryKey: [...KLIC_ZAMESTNANCI, 'karta', id],
    queryFn: ({ signal }) => nacistZamestnanceKartu(id as string, signal),
    enabled: Boolean(id),
  });
}

/** Výběr nadřízeného a zástupu: celá firma na jedné stránce (i ukončení — kvůli stávajícím vazbám). */
export const VELIKOST_VYBERU = 500;

export function nacistVyberZamestnancu(signal?: AbortSignal): Promise<ZamestnanecSeznam> {
  return api.get<ZamestnanecSeznam>(
    `/zamestnanci?strana=1&velikost=${VELIKOST_VYBERU}&razeni=prijmeni:asc,jmeno:asc`,
    { signal },
  );
}

export function useVyberZamestnancu(povoleno = true) {
  return useQuery({
    queryKey: [...KLIC_ZAMESTNANCI, 'vyber'],
    queryFn: ({ signal }) => nacistVyberZamestnancu(signal),
    enabled: povoleno,
  });
}

/** Odpověď GET /zamestnanci/jmena — jen `{ id, jmeno }` aktivních kolegů (05/235), bez karet. */
export type JmenaKolegu =
  operations['zamestnanciJmena']['responses'][200]['content']['application/json'];
export type JmenoKolegy = JmenaKolegu['polozky'][number];

/**
 * GET /zamestnanci/jmena — jména aktivních kolegů pro „Kdo se zranil“ v ⚑ Nahlásit (rozhodnutí zadavatele
 * 27. 9. 2026, 05/235). Stačí `uraz.zapsat` (má ho každý), karta zaměstnance potřeba není.
 */
export function nacistJmenaKolegu(signal?: AbortSignal, role?: string): Promise<JmenaKolegu> {
  const dotaz = role ? `?role=${encodeURIComponent(role)}` : '';
  return api.get<JmenaKolegu>(`/zamestnanci/jmena${dotaz}`, { signal, bezHlaseniSite: true });
}

/** Jména aktivních kolegů; s `role` jen kolegové v té roli (hlavní nebo navíc — výběr řidiče jménem, 05/4330 a). */
export function useJmenaKolegu(povoleno = true, role?: string) {
  return useQuery({
    queryKey: [...KLIC_ZAMESTNANCI, 'jmena', role ?? ''],
    queryFn: ({ signal }) => nacistJmenaKolegu(signal, role),
    enabled: povoleno,
    // Seznam lidí se mění zřídka — drawer ho při každém otevření znovu nestahuje.
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Člověk ve výběru „kdo“ (odpovědná osoba, vážný, účastník) — jen to, co výběr potřebuje. */
export interface ClovekKVyberu {
  id: string;
  /** `Jméno Příjmení`. */
  jmeno: string;
  aktivni: boolean;
  /** Role jen z karty (`zamestnanec.cist_zakladni` / `zamestnanec.cist`); ze jmen kolegů nepřijde. */
  role?: string;
}

/** Smí účet číst karty zaměstnanců (seznam `GET /zamestnanci`)? */
export function smiKartyZamestnancu(akce: readonly string[] | null | undefined): boolean {
  return Boolean(
    akce?.includes('zamestnanec.cist_zakladni') ||
    akce?.includes('zamestnanec.cist') ||
    akce?.includes('zamestnanec.cist_bez_penez'),
  );
}

/**
 * Lidé do výběru „kdo“ na obrazovkách, které mají i role bez karty zaměstnance (skladník, řidič, mlynář — rozhodnutí
 * zadavatele 30. 10. 2026): s právem karty ze seznamu zaměstnanců (i neaktivní, kvůli stávajícím vazbám), bez něj
 * jen jména aktivních kolegů z `GET /zamestnanci/jmena` — žádné kontakty, role ani stav účtu.
 */
export function useLideKVyberu(akce: readonly string[] | null | undefined, povoleno = true) {
  const smiKartu = smiKartyZamestnancu(akce);
  const karty = useVyberZamestnancu(povoleno && smiKartu);
  const jmena = useJmenaKolegu(povoleno && !smiKartu);
  const dotaz = smiKartu ? karty : jmena;
  const lide = useMemo<ClovekKVyberu[]>(() => {
    if (smiKartu) {
      return (karty.data?.polozky ?? [])
        .filter((z): z is Zamestnanec & { id: string } => Boolean(z.id))
        .map((z) => ({
          id: z.id,
          jmeno: `${z.jmeno} ${z.prijmeni}`.trim(),
          aktivni: Boolean(z.aktivni && !z.ukoncen),
          role: z.role,
        }));
    }
    return (jmena.data?.polozky ?? []).map((z) => ({ id: z.id, jmeno: z.jmeno, aktivni: true }));
  }, [smiKartu, karty.data, jmena.data]);
  return { lide, isPending: dotaz.isPending, isError: dotaz.isError, error: dotaz.error };
}

/**
 * POST /zamestnanci — založí kartu i účet (`zamestnanec.ucet` + `zamestnanec.upravit`); duplicitní
 * e‑mail 409 `email_obsazen` s důvodem, roli admin přidělí jen admin (403).
 */
export function zalozitZamestnance(telo: ZamestnanecVstup): Promise<Zamestnanec> {
  return api.post<Zamestnanec>('/zamestnanci', telo);
}

/**
 * PUT /zamestnanci/{id} — celý vstup (`zamestnanec.upravit`; e‑mail a úvazek navíc `zamestnanec.ucet`).
 * Pole, která server nevrátil (bez práva), se neposílají a zůstanou beze změny.
 */
export function upravitZamestnance(id: string, telo: ZamestnanecVstup): Promise<Zamestnanec> {
  return api.put<Zamestnanec>(`/zamestnanci/${encodeURIComponent(id)}`, telo);
}

/** POST /zamestnanci/{id}/ucet — role povinná, ostatní pole se mění jen když jsou zaslaná. */
export function ulozitUcet(id: string, telo: ZamestnanecUcetVstup): Promise<Zamestnanec> {
  return api.post<Zamestnanec>(`/zamestnanci/${encodeURIComponent(id)}/ucet`, telo);
}

/** Klíč react-query sekcí navíc jednoho účtu. */
export function klicSekciNavic(id: string) {
  return [...KLIC_ZAMESTNANCI, 'sekce-navic', id] as const;
}

/** GET /zamestnanci/{id}/sekce-navic — uložené sekce navíc, sekce z role a nabídka po kategoriích. */
export function nacistSekceNavic(id: string, signal?: AbortSignal): Promise<SekceNavicPrehled> {
  return api.get<SekceNavicPrehled>(`/zamestnanci/${encodeURIComponent(id)}/sekce-navic`, {
    signal,
  });
}

export function useSekceNavic(id: string | undefined) {
  return useQuery({
    queryKey: klicSekciNavic(id ?? ''),
    queryFn: ({ signal }) => nacistSekceNavic(id as string, signal),
    enabled: Boolean(id),
  });
}

/** PUT /zamestnanci/{id}/sekce-navic — celý seznam sekcí navíc; nepřidatelné sekce server odmítne (400). */
export function ulozitSekceNavic(id: string, telo: SekceNavicVstup): Promise<SekceNavicPrehled> {
  return api.put<SekceNavicPrehled>(`/zamestnanci/${encodeURIComponent(id)}/sekce-navic`, telo);
}

// --- Role navíc (08 § 1 bod 3a, rozhodnutí 15. 9. 2026 večer) ---------------------------------------------

/** Klíč react-query rolí jednoho účtu. */
export function klicRoli(id: string) {
  return [...KLIC_ZAMESTNANCI, 'role', id] as const;
}

/** GET /zamestnanci/{id}/role — hlavní role, role navíc (kdo a kdy je přidal) a nabídka rolí k přidání. */
export function nacistRole(id: string, signal?: AbortSignal): Promise<RolePrehled> {
  return api.get<RolePrehled>(`/zamestnanci/${encodeURIComponent(id)}/role`, { signal });
}

export function useRoleUctu(id: string | undefined) {
  return useQuery({
    queryKey: klicRoli(id ?? ''),
    queryFn: ({ signal }) => nacistRole(id as string, signal),
    enabled: Boolean(id),
  });
}

/** POST /zamestnanci/{id}/role — přidá roli navíc (práva se sčítají; hlavní role se nemění). */
export function pridatRoli(id: string, telo: RoleNavicVstup): Promise<RolePrehled> {
  return api.post<RolePrehled>(`/zamestnanci/${encodeURIComponent(id)}/role`, telo);
}

/** DELETE /zamestnanci/{id}/role/{role} — odebere roli navíc; práva mizí hned. */
export function odebratRoli(id: string, role: string): Promise<RolePrehled> {
  return api.delete<RolePrehled>(
    `/zamestnanci/${encodeURIComponent(id)}/role/${encodeURIComponent(role)}`,
  );
}

/** POST /zamestnanci/{id}/ukoncit — účet se nepřihlásí, relace se zneplatní. */
export function ukoncitZamestnance(
  id: string,
  telo: ZamestnanecUkonceniVstup,
): Promise<Zamestnanec> {
  return api.post<Zamestnanec>(`/zamestnanci/${encodeURIComponent(id)}/ukoncit`, telo);
}

/** POST /zamestnanci/{id}/obnovit — zrušení ukončení (omyl). */
export function obnovitZamestnance(id: string): Promise<Zamestnanec> {
  return api.post<Zamestnanec>(`/zamestnanci/${encodeURIComponent(id)}/obnovit`);
}

/** Dnešní datum ve tvaru RRRR-MM-DD (místní čas) pro `datum_ukonceni`. */
export function dnesIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// --- Pojistky obchodních dat (rozhodnutí zadavatele 7. 10. 2026) ------------------------------------------------

/** Omezený režim ve výpovědní lhůtě (PUT /zamestnanci/{id}/omezeny-rezim). */
export type OmezenyRezim = components['schemas']['OmezenyRezim'];
/** „Co si prohlížel“ — záznam čtení obchodních dat (GET /zamestnanci/{id}/cteni-log). */
export type CteniLogPrehled = components['schemas']['CteniLogPrehled'];
export type CteniLogZaznam = components['schemas']['CteniLogZaznam'];

/** Akce přehledu „Co si prohlížel“ (08 § 3.13, jen A J). */
export const AKCE_CTENI_LOG = 'zamestnanec.cteni_log';

/** PUT /zamestnanci/{id}/omezeny-rezim — zapne / vypne omezený režim (smí `zamestnanec.ucet`, audit). */
export function nastavitOmezenyRezim(id: string, zapnuto: boolean): Promise<OmezenyRezim> {
  return api.put<OmezenyRezim>(`/zamestnanci/${encodeURIComponent(id)}/omezeny-rezim`, {
    zapnuto,
  });
}

export function klicCteniLog(id: string, dni: number) {
  return [...KLIC_ZAMESTNANCI, 'cteni-log', id, dni] as const;
}

/** GET /zamestnanci/{id}/cteni-log — souhrn po dnech a poslední záznamy (výchozí 30 dní). */
export function useCteniLog(id: string, dni = 30, povoleno = true) {
  return useQuery({
    queryKey: klicCteniLog(id, dni),
    queryFn: ({ signal }) =>
      api.get<CteniLogPrehled>(
        `/zamestnanci/${encodeURIComponent(id)}/cteni-log?dni=${encodeURIComponent(String(dni))}`,
        { signal },
      ),
    enabled: povoleno && Boolean(id),
  });
}

/** Role, ze kterých se vybírá řidič zakázky (řidič a koordinátorka, která také jezdí). */
const ROLE_RIDICU = ['ridic', 'koordinator'] as const;

/** Řidič do výběru — `prijmeni` je u jmen kolegů prázdné (jméno je celé v `jmeno`). */
export interface RidicKVyberu {
  id: string;
  jmeno: string;
  prijmeni: string;
}

/**
 * Řidiči do výběru u zakázky (řidič, koordinátorka): s právem karty ze seznamu zaměstnanců (aktivní a právě vybraný),
 * bez něj — skladník u zakázek skladu — jen jména kolegů v těch rolích (`GET /zamestnanci/jmena?role=…`,
 * rozhodnutí zadavatele 30. 10. 2026: skladník o zaměstnancích nevidí nic; řidič se vybírá z našich jménem, 05/4330 a).
 */
export function useRidiciKVyberu(
  akce: readonly string[] | null | undefined,
  povoleno = true,
  vybranyId?: string | null,
) {
  const smiKartu = smiKartyZamestnancu(akce);
  const karty = useVyberZamestnancu(povoleno && smiKartu);
  // Jména v roli řidiče / koordinátora i jako role navíc (server filtruje hlavní i navíc) — u účtu bez karet jsou to
  // celé kandidáty, u účtu s kartou doplní lidi, kteří roli řidiče mají jen navíc (seznam karet nese jen hlavní roli).
  const ridici = useJmenaKolegu(povoleno, ROLE_RIDICU[0]);
  const koordinatori = useJmenaKolegu(povoleno, ROLE_RIDICU[1]);
  const zaklad = useMemo<RidicKVyberu[]>(() => {
    if (smiKartu) {
      const sRoliNavic = new Set(
        [...(ridici.data?.polozky ?? []), ...(koordinatori.data?.polozky ?? [])].map((z) => z.id),
      );
      return (karty.data?.polozky ?? [])
        .filter(
          (z): z is Zamestnanec & { id: string } =>
            Boolean(z.id) &&
            ((((ROLE_RIDICU as readonly string[]).includes(z.role) || sRoliNavic.has(z.id ?? '')) &&
              z.aktivni &&
              !z.ukoncen) ||
              z.id === vybranyId),
        )
        .map((z) => ({ id: z.id, jmeno: z.jmeno, prijmeni: z.prijmeni }));
    }
    const vsichni = [...(ridici.data?.polozky ?? []), ...(koordinatori.data?.polozky ?? [])];
    const videni = new Set<string>();
    return vsichni
      .filter((z) => !videni.has(z.id) && Boolean(videni.add(z.id)))
      .map((z) => ({ id: z.id, jmeno: z.jmeno, prijmeni: '' }));
  }, [smiKartu, karty.data, ridici.data, koordinatori.data, vybranyId]);
  const chyba = smiKartu ? karty.error : (ridici.error ?? koordinatori.error);
  // Právě vybraný řidič, který v nabídce není (odešel, plánovací pohled vidí jen aktivní): jméno podle id, ať pole
  // u starého záznamu neukazuje prázdno.
  const chybiVybrany = Boolean(povoleno && vybranyId && !zaklad.some((r) => r.id === vybranyId));
  const vybrany = useQuery({
    queryKey: [...KLIC_ZAMESTNANCI, 'jmena', 'id', vybranyId ?? ''],
    queryFn: ({ signal }) =>
      api.get<JmenaKolegu>(`/zamestnanci/jmena?id=${encodeURIComponent(vybranyId ?? '')}`, {
        signal,
        bezHlaseniSite: true,
      }),
    enabled: chybiVybrany,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const seznam = useMemo<RidicKVyberu[]>(() => {
    const navic = chybiVybrany
      ? (vybrany.data?.polozky ?? []).map((z) => ({ id: z.id, jmeno: z.jmeno, prijmeni: '' }))
      : [];
    return [...navic, ...zaklad];
  }, [zaklad, chybiVybrany, vybrany.data]);
  return {
    ridici: seznam,
    isError: smiKartu ? karty.isError : ridici.isError || koordinatori.isError,
    error: chyba,
    nacteno: smiKartu ? Boolean(karty.data) : Boolean(ridici.data && koordinatori.data),
  };
}
